import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Check, ChevronDown, ChevronUp, Shield } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'webysee_cookie_consent';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  // Check if consent was already given
  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      if (callback) callback();
    }, 300);
  };

  const saveConsent = (consentData) => {
    const data = {
      ...consentData,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(data));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted);
    handleClose();
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(onlyNecessary);
    handleClose();
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    handleClose();
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[998] transition-opacity duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleRejectAll}
      />

      {/* Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[999] transition-all duration-500 ${
          isClosing ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 pb-4">
          <div className="bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">
            {/* Main Content */}
            <div className="p-6 md:p-8">
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:items-start">
                {/* Icon & Text */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-accent/10 flex items-center justify-center">
                      <Cookie className="w-5 h-5 text-accent" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Nous respectons votre vie privée
                    </h3>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    Nous utilisons des cookies pour améliorer votre expérience sur notre site, 
                    analyser le trafic et personnaliser le contenu. Vous pouvez choisir les cookies 
                    que vous acceptez.{' '}
                    <Link 
                      to="/politique-confidentialite" 
                      className="text-accent hover:underline"
                    >
                      En savoir plus
                    </Link>
                  </p>

                  {/* Toggle Settings Button */}
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
                  >
                    <Settings size={16} />
                    Personnaliser mes choix
                    {showSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 lg:flex-col xl:flex-row">
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-3 bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition-all duration-300"
                  >
                    Refuser tout
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="relative overflow-hidden px-6 py-3 bg-accent text-black text-sm font-medium group"
                  >
                    <span className="absolute inset-0 overflow-hidden">
                      <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                    </span>
                    <span className="relative z-10">Accepter tout</span>
                  </button>
                </div>
              </div>

              {/* Settings Panel */}
              <div
                className={`overflow-hidden transition-all duration-500 ${
                  showSettings ? 'max-h-[500px] opacity-100 mt-6' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="border-t border-white/10 pt-6">
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Necessary Cookies */}
                    <div className="p-4 bg-white/[0.02] border border-white/[0.05]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Shield size={16} className="text-accent" />
                          <span className="text-white font-medium text-sm">Essentiels</span>
                        </div>
                        <div className="w-10 h-5 bg-accent/30 rounded-full flex items-center px-0.5">
                          <div className="w-4 h-4 bg-accent rounded-full ml-auto" />
                        </div>
                      </div>
                      <p className="text-white/50 text-xs leading-relaxed">
                        Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.
                      </p>
                      <span className="inline-block mt-2 px-2 py-0.5 bg-accent/10 text-accent text-xs">
                        Toujours actifs
                      </span>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="p-4 bg-white/[0.02] border border-white/[0.05]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white font-medium text-sm">Analytiques</span>
                        <button
                          onClick={() => togglePreference('analytics')}
                          className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-300 ${
                            preferences.analytics ? 'bg-accent/30' : 'bg-white/10'
                          }`}
                        >
                          <div 
                            className={`w-4 h-4 rounded-full transition-all duration-300 ${
                              preferences.analytics 
                                ? 'bg-accent ml-auto' 
                                : 'bg-white/40 ml-0'
                            }`} 
                          />
                        </button>
                      </div>
                      <p className="text-white/50 text-xs leading-relaxed">
                        Nous aident à comprendre comment vous utilisez le site pour l'améliorer.
                      </p>
                    </div>

                    {/* Marketing Cookies */}
                    <div className="p-4 bg-white/[0.02] border border-white/[0.05]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white font-medium text-sm">Marketing</span>
                        <button
                          onClick={() => togglePreference('marketing')}
                          className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-300 ${
                            preferences.marketing ? 'bg-accent/30' : 'bg-white/10'
                          }`}
                        >
                          <div 
                            className={`w-4 h-4 rounded-full transition-all duration-300 ${
                              preferences.marketing 
                                ? 'bg-accent ml-auto' 
                                : 'bg-white/40 ml-0'
                            }`} 
                          />
                        </button>
                      </div>
                      <p className="text-white/50 text-xs leading-relaxed">
                        Permettent de personnaliser les publicités et mesurer leur efficacité.
                      </p>
                    </div>
                  </div>

                  {/* Save Preferences Button */}
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleSavePreferences}
                      className="flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 transition-all duration-300"
                    >
                      <Check size={16} />
                      Enregistrer mes préférences
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom accent line */}
            <div className="h-1 bg-gradient-to-r from-accent via-violet-500 to-accent" />
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Hook to get cookie consent status
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  const hasConsent = (type) => {
    if (!consent) return false;
    return consent[type] === true;
  };

  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.location.reload();
  };

  return { consent, hasConsent, resetConsent };
};

export default CookieBanner;
